import { useEffect, useState } from "react";
import { useParams, Link } from "react-router";
import { api } from "../lib/api";
import { Badge } from "../components/primitives/Badge";
import { Pulse } from "../components/primitives/Pulse";

interface Connector {
  connectorType: string;
  label: string;
  capabilities: string[];
  ingest?: {
    webhook?: { signatureScheme: string };
    poll?: object;
    stream?: object;
    sqs?: object;
  };
  documentation?: {
    summary?: string;
    capabilityDocs?: Record<string, string>;
    setupSteps?: string[];
    requiredScopes?: string[];
  } | null;
}

export function CatalogConnector() {
  const { type } = useParams<{ type: string }>();
  const [connector, setConnector] = useState<Connector | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.listConnectors().then((r) => {
      const found = (r.connectors as Connector[]).find((c) => c.connectorType === type) ?? null;
      setConnector(found);
    }).catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [type]);

  if (error) return <p className="page-help">{error}</p>;
  if (!connector) return <Pulse />;

  const docs = connector.documentation;

  return (
    <div style={{ maxWidth: 800 }}>
      <div style={{ marginBottom: 8 }}>
        <Link to="/catalog" className="uppercase-label">← CATALOG</Link>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div>
          <div className="uppercase-label">CONNECTOR</div>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: 32, margin: "4px 0 8px" }}>
            {connector.label}
          </h1>
          <span className="mono" style={{ color: "var(--color-text-secondary)" }}>{connector.connectorType}</span>
        </div>
        <span style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {connector.capabilities.map((c) => <Badge key={c} tone="ok">{c.toUpperCase()}</Badge>)}
        </span>
      </div>

      {docs?.summary && <p className="page-help" style={{ marginTop: 16 }}>{docs.summary}</p>}

      {docs?.capabilityDocs && Object.keys(docs.capabilityDocs).length > 0 && (
        <section style={{ marginTop: 32 }}>
          <div className="uppercase-label" style={{ marginBottom: 8 }}>CAPABILITIES</div>
          <dl style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "8px 16px" }}>
            {Object.entries(docs.capabilityDocs).map(([cap, copy]) => (
              <div key={cap} style={{ display: "contents" }}>
                <dt><Badge tone="ok">{cap.toUpperCase()}</Badge></dt>
                <dd className="page-help" style={{ margin: 0 }}>{copy}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {docs?.setupSteps && docs.setupSteps.length > 0 && (
        <section style={{ marginTop: 32 }}>
          <div className="uppercase-label" style={{ marginBottom: 8 }}>SETUP</div>
          <ol style={{ color: "var(--color-text-secondary)", fontSize: 12, lineHeight: 1.7, paddingLeft: 24 }}>
            {docs.setupSteps.map((s, i) => <li key={i} style={{ marginBottom: 6 }}>{s}</li>)}
          </ol>
        </section>
      )}

      {docs?.requiredScopes && docs.requiredScopes.length > 0 && (
        <section style={{ marginTop: 32 }}>
          <div className="uppercase-label" style={{ marginBottom: 8 }}>REQUIRED SCOPES</div>
          <ul style={{ display: "flex", flexWrap: "wrap", gap: 6, listStyle: "none", padding: 0 }}>
            {docs.requiredScopes.map((s) => (
              <li key={s} className="mono" style={{ border: "1px solid var(--color-border)", padding: "4px 8px", fontSize: 11 }}>
                {s}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
